import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import { useAuth } from "../hooks/useAuth";
import { useTenant } from "../hooks/useTenant";
import useRequests from "../hooks/http";

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { activeTenant, loading: tenantLoading } = useTenant();
  const { get } = useRequests();
  const navigate = useNavigate();

  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeTenant) {
      setApplications([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    get(`/admin/tenants/${activeTenant.id}/applications`).then(({ data, status }) => {
      if (status === 200 && Array.isArray(data)) setApplications(data);
      setLoading(false);
    });
  }, [activeTenant, get]);

  if (tenantLoading || loading) {
    return <LinearProgress />;
  }

  if (!activeTenant) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Welcome back, {user?.first_name ?? user?.email}
        </Typography>
        <Typography color="text.secondary">
          No tenants found. You may not have any tenant memberships yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Welcome back, {user?.first_name ?? user?.email}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
        {activeTenant.name} workspace
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("dashboard.description")}
      </Typography>

      <Typography variant="h6" gutterBottom>
        Applications ({applications.length})
      </Typography>

      {applications.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          No applications registered in this tenant yet.
        </Typography>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {applications.map((app) => (
            <Grid item xs={12} sm={6} md={4} key={app.id}>
              <Card variant="outlined">
                <CardActionArea onClick={() => navigate(`/app/applications/${app.id}`)}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <AppsIcon color="primary" />
                      <Typography variant="h6">{app.name}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {app.identifier}
                    </Typography>
                    {app.description && (
                      <Typography variant="body2">{app.description}</Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

    </Box>
  );
}
